import React, {Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Button, Card, InputGroup, Menu, MenuItem, Popover, Tag, Tooltip} from "@blueprintjs/core";
import {APIProvider as MapsAPIProvider, Map, AdvancedMarker, Pin} from '@vis.gl/react-google-maps';
import styled from "styled-components";

import {SchemaForm} from "../../../components/forms";
import {StyledFormGroup} from "../../../components/forms/fields/shared";
import {AvailableTimezones, AvailableCountryCodes, AvailableCurrencyCodes, OpeningTimes} from "./properties";
import {GOOGLE_MAPS_API_KEY, GOOGLE_TIMEZONE_API_KEY} from '../../../config/vars';
import {entityTags, locations, organisations} from "../../../services/api";
import {errorToast, successToast} from "../../../utils/toaster";
import {DataPageContext} from "../context";
import {TAG_TYPES, TAG_VALUE_PATTERN, TAG_VALUE_MAX_LENGTH, formatTag} from "../../../constants/tags";


const TagSelectButton = styled(Button)`
    &[class*="bp"][class*="-button"] {
        border-radius: var(--sf-border-radius, 20px) !important;
        width: ${p => p.fill ? '100%' : 'fit-content'};
        justify-content: space-between;
        background: transparent !important;
        box-shadow: inset 0 0 0 1px rgba(17, 20, 24, 0.2) !important;
    }
`;

const TagButtonContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    position: relative;
`;

const HiddenTagsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px;
`;

const MeasureRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    white-space: nowrap;
`;

const TagRemoveBtn = styled.span`
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    border-radius: 0 10px 10px 0;
    background: rgba(205, 66, 70, 0.75);
    color: white;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s;
`;

const HoverRemoveTag = styled(Tag)`
    position: relative;
    &:hover ${TagRemoveBtn} {
        opacity: 1;
    }
`;


const TagPopoverContent = styled.div`
    display: flex;
    flex-direction: column;
    width: min-content;
`;

const TagPanelContainer = styled.div`
    display: flex;
`;

const TagPanel = styled.div`
    min-width: 180px;
    max-height: 380px;
    display: flex;
    flex-direction: column;

    &:not(:first-child) {
        border-left: 1px solid rgba(17, 20, 24, 0.15);
    }
`;

const TagPanelHeader = styled.div`
    padding: 6px 10px;
    font-weight: 600;
    font-size: 11px;
    color: #5c7080;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(17, 20, 24, 0.15);
    white-space: nowrap;
`;

const TagPanelMenu = styled(Menu)`
    overflow-y: auto;
    flex: 1;
    max-height: none;
`;

const TagInputBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 6px 7px;
    border-bottom: 1px solid rgba(17, 20, 24, 0.15);
`;

const TagInputRow = styled.div`
    padding: 6px 7px;
    border-top: 1px solid rgba(17, 20, 24, 0.15);
    display: flex;
    gap: 4px;

    & [class*="bp"][class*="-input-group"],
    & [class*="bp"][class*="-input"] {
        border-radius: var(--sf-border-radius, 20px) !important;
    }
`;

const tagKey = (tag) => tag.id || tag.tagType + ':' + tag.tagValue;

const sameTag = (a, b) => a.id ? a.id === b.id : (a.tagType === b.tagType && a.tagValue === b.tagValue);

const TagsRenderer = ({label, value, disabled, options, onChange}) => {
    const [focusedType, setFocusedType] = useState(null);
    const [customValue, setCustomValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const tags = Array.isArray(value) ? value : [];
    const availableTags = options?.availableTags || [];

    // Display: show assigned tags as Tag components, auto-fit
    const assigned = useMemo(() =>
        [...tags].sort((a, b) => {
            const ai = TAG_TYPES.findIndex(t => t.value === a.tagType);
            const bi = TAG_TYPES.findIndex(t => t.value === b.tagType);
            return ai !== bi ? ai - bi : a.tagValue.localeCompare(b.tagValue);
        }),
    [tags]);

    const entriesOfFocusedType = useMemo(() =>
        focusedType ? tags.filter(t => t.tagType === focusedType) : [],
    [tags, focusedType]);

    // Value suggestions for the focused type (catalog values + current values)
    const suggestions = useMemo(() => {
        if (!focusedType) return [];
        const catalogEntries = availableTags.filter(t => t.tagType === focusedType);
        const missing = entriesOfFocusedType.filter(a => !catalogEntries.some(c => sameTag(a, c)));
        return [...catalogEntries, ...missing].sort((a, b) => a.tagValue.localeCompare(b.tagValue));
    }, [focusedType, availableTags, entriesOfFocusedType]);

    const isAssigned = useCallback((entry) =>
        tags.some(t => sameTag(entry, t)),
    [tags]);

    const setTagValue = useCallback((type, entry) => {
        const filtered = tags.filter(t => t.tagType !== type);
        if (entry) filtered.push(entry);
        onChange(filtered);
    }, [tags, onChange]);

    const handleCustomAdd = useCallback(() => {
        const trimmed = customValue.trim();
        if (!focusedType || !trimmed || !TAG_VALUE_PATTERN.test(trimmed) || trimmed.length > TAG_VALUE_MAX_LENGTH) return;

        const catalogOfType = availableTags.filter(t => t.tagType === focusedType);
        const exact = catalogOfType.find(t => t.tagValue === trimmed);
        if (exact) {
            setTagValue(focusedType, exact);
            setCustomValue('');
            return;
        }

        const caseInsensitive = catalogOfType.find(t => t.tagValue.toLowerCase() === trimmed.toLowerCase());
        if (caseInsensitive) {
            const typeLabel = TAG_TYPES.find(t => t.value === focusedType)?.label;
            if (!window.confirm(`"${trimmed}" already exists for ${typeLabel} as "${caseInsensitive.tagValue}"\nCreate it anyway?`)) return;
            setTagValue(focusedType, {tagType: focusedType, tagValue: trimmed});
            setCustomValue('');
            return;
        }

        setTagValue(focusedType, {tagType: focusedType, tagValue: trimmed});
        setCustomValue('');
    }, [focusedType, customValue, availableTags, setTagValue]);

    const handleCustomInput = useCallback((e) => {
        setCustomValue(e.target.value);
    }, []);

    const handleRemoveEntry = useCallback((entry) => {
        const key = tagKey(entry);
        onChange(tags.filter(t => tagKey(t) !== key));
    }, [tags, onChange]);

    const measureRef = useRef(null);
    const targetRef = useRef(null);
    const [fitCount, setFitCount] = useState(assigned.length);

    const computeFit = useCallback(() => {
        const measure = measureRef.current;
        const target = targetRef.current;
        if (!measure || !target || !assigned.length) return;

        const available = target.clientWidth - 60;
        const children = measure.children;
        if (!children.length || available <= 0) return;

        const badgeWidth = children[children.length - 1].offsetWidth + 4;
        let used = 0;
        let fit = 0;

        for (let i = 0; i < assigned.length; i++) {
            const tagWidth = children[i].offsetWidth + (i > 0 ? 4 : 0);
            const isLast = i === assigned.length - 1;
            const spaceNeeded = used + tagWidth + (isLast ? 0 : badgeWidth);
            if (spaceNeeded > available && fit > 0) break;
            used += tagWidth + (i > 0 ? 4 : 0);
            fit++;
        }

        if (fit !== fitCount) setFitCount(fit);
    }, [assigned, fitCount]);

    const measureRefCallback = useCallback((node) => {
        measureRef.current = node;
        if (node) {
            const formGroup = node.closest('[class*="bp"][class*="-form-group"]');
            targetRef.current = formGroup || node.parentElement;
        }
    }, []);

    useLayoutEffect(() => {
        const target = targetRef.current;
        if (!target) return;
        computeFit();
        const ro = new ResizeObserver(computeFit);
        ro.observe(target);
        return () => ro.disconnect();
    }, [computeFit]);

    const buttonContent = assigned.length === 0 ? null : (
        <TagButtonContent>
            <MeasureRow ref={measureRefCallback}>
                {assigned.map(t => (
                    <Tag key={tagKey(t)} minimal small round intent="primary">{formatTag(t)}</Tag>
                ))}
                <Tag minimal small round>+{assigned.length}</Tag>
            </MeasureRow>
            {assigned.slice(0, fitCount).map(t => (
                <Tag key={tagKey(t)} minimal small round intent="primary">{formatTag(t)}</Tag>
            ))}
            {assigned.length > fitCount && (
                <Tooltip
                    placement="bottom"
                    hoverOpenDelay={200}
                    content={
                        <HiddenTagsList>
                            {assigned.map(t => (
                                <Tag key={tagKey(t)} small round>{formatTag(t)}</Tag>
                            ))}
                        </HiddenTagsList>
                    }
                >
                    <Tag minimal small round interactive>+{assigned.length - fitCount}</Tag>
                </Tooltip>
            )}
        </TagButtonContent>
    );

    return (
        <StyledFormGroup label={label}>
            <Popover
                disabled={disabled}
                minimal
                fill={!!options?.fill}
                placement="bottom-start"
                usePortal={true}
                isOpen={isOpen}
                onInteraction={(nextOpen) => {
                    setIsOpen(nextOpen);
                    if (nextOpen) { setFocusedType(null); setCustomValue(''); }
                }}
                content={
                    <TagPopoverContent>
                        {assigned.length > 0 && (
                            <TagInputBar>
                                {assigned.map(t => (
                                    <HoverRemoveTag key={tagKey(t)} minimal small round intent="primary">
                                        {formatTag(t)}
                                        {!disabled && <TagRemoveBtn onClick={() => handleRemoveEntry(t)}>&times;</TagRemoveBtn>}
                                    </HoverRemoveTag>
                                ))}
                            </TagInputBar>
                        )}
                        <TagPanelContainer>
                            <TagPanel>
                                <TagPanelHeader>Tag Type</TagPanelHeader>
                                <TagPanelMenu>
                                    {TAG_TYPES.map(type => {
                                        const entriesOfType = assigned.filter(t => t.tagType === type.value);
                                        return (
                                            <MenuItem
                                                key={type.value}
                                                text={type.label}
                                                active={focusedType === type.value}
                                                icon={entriesOfType.length ? 'tick' : 'blank'}
                                                labelElement={entriesOfType.length ? <span style={{fontSize: 11, color: '#5c7080'}}>{entriesOfType.map(e => e.tagValue).join(', ')}</span> : null}
                                                title={type.description}
                                                onClick={() => { setFocusedType(type.value); setCustomValue(''); }}
                                                shouldDismissPopover={false}
                                            />
                                        );
                                    })}
                                </TagPanelMenu>
                            </TagPanel>
                            {focusedType && (
                                <TagPanel>
                                    <TagPanelHeader>
                                        {TAG_TYPES.find(t => t.value === focusedType)?.label} Value
                                    </TagPanelHeader>
                                    <TagPanelMenu>
                                        {entriesOfFocusedType.length > 0 && (
                                            <MenuItem
                                                icon="cross"
                                                text={<em>Clear</em>}
                                                intent="danger"
                                                onClick={() => setTagValue(focusedType, null)}
                                                shouldDismissPopover={false}
                                            />
                                        )}
                                        {suggestions.map(entry => (
                                            <MenuItem
                                                key={tagKey(entry)}
                                                text={entry.tagValue}
                                                active={isAssigned(entry)}
                                                icon={isAssigned(entry) ? 'tick' : 'blank'}
                                                onClick={() => setTagValue(focusedType, entry)}
                                                shouldDismissPopover={false}
                                            />
                                        ))}
                                    </TagPanelMenu>
                                    <TagInputRow>
                                        <InputGroup
                                            value={customValue}
                                            onChange={handleCustomInput}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomAdd(); } }}
                                            placeholder="Custom value..."
                                            small
                                            fill
                                        />
                                        <Button
                                            icon="add"
                                            small
                                            minimal
                                            intent="success"
                                            disabled={!customValue.trim() || !TAG_VALUE_PATTERN.test(customValue.trim()) || customValue.trim().length > TAG_VALUE_MAX_LENGTH}
                                            onClick={handleCustomAdd}
                                        />
                                    </TagInputRow>
                                </TagPanel>
                            )}
                        </TagPanelContainer>
                    </TagPopoverContent>
                }
            >
                <TagSelectButton
                    text={buttonContent || 'Select tags\u2026'}
                    rightIcon="chevron-down"
                    disabled={disabled}
                    fill={!!options?.fill}
                />
            </Popover>
        </StyledFormGroup>
    );
};

const _LocSchema = ({...props}) => {
    return {
        type: "object",
        required: [
            "id",
            "name",
            "organisation",
            "status",
            "timezone",
            "coordinates",
            "openingTimes"
        ],
        properties: {
            id: {
                type: "string",
                pattern: "^[a-f\\d]{23,26}$",
                readOnly: true
            },
            name: {
                type: "string"
            },
            createdAt: {
                type: "string",
                readOnly: true
            },
            updatedAt: {
                type: "string",
                readOnly: true
            },

            organisation: {
                oneOf: props.availableOrganisations ?
                    props.availableOrganisations.map(org => ({const: org.id, title: org.name}))
                    : []
            },

            status: {
                type: "string",
                enum: ['created', 'setting up', 'operational', 'near contract end', 'contract ended'],
            },
            goLiveDate: {
                type: "string",
                format: "date-time",
            },

            timezone: {
                type: "string",
                enum: AvailableTimezones,
            },
            currency: {
                type: "string",
                enum: AvailableCurrencyCodes,
            },
            countryCode: {
                type: "string",
                enum: AvailableCountryCodes,
            },
            salesDataId: {
                type: "string",
            },
            tags: {
                type: "array",
                items: {
                    type: "object",
                    properties: {
                        id: {type: "string", pattern: "^[a-f\\d]{23,26}$"},
                        tagType: {type: "string", enum: TAG_TYPES.map(t => t.value)},
                        tagValue: {type: "string", minLength: 1, maxLength: 128, pattern: "^[^\\n]+$"},
                    },
                    required: ["tagType", "tagValue"],
                },
            },
            coordinates: {
                type: "string",
                pattern: "(?<lat>^[-+]?(?:[1-8]?\\d(?:\\.\\d+)?|90(?:\\.0+)?))\\s*,\\s*(?<lng>[-+]?(?:180(?:\\.0+)?|(?:1[0-7]\\d|[1-9]?\\d)(?:\\.\\d+)?))$"
            },

            openingTimes: {
                type: "array",
                minItems: 7,
                maxItems: 7,
                items: {
                    type: "object",
                    properties: {
                        day: {type: "integer", minimum: 0, maximum: 6},
                        startHour: {type: "integer", minimum: 0, maximum: 23},
                        startMinute: {type: "integer", minimum: 0, maximum: 59},
                        stopHour: {type: "integer", minimum: 0, maximum: 23},
                        stopMinute: {type: "integer", minimum: 0, maximum: 59},
                    }
                }
            },
        }
    }
}


const _UILocSchema = ({...props}) => ({
    type: "VerticalLayout",
    elements: [
        {
            type: "Group",
            label: "Information",
            elements: [
                {
                    type: "HorizontalLayout",
                    elements: [
                        {
                            type: "Control",
                            scope: "#/properties/name",
                        },
                        {
                            type: "Control",
                            scope: "#/properties/id",
                            options: {disabled: true}
                        },
                        {
                            type: "Control",
                            scope: "#/properties/createdAt",
                            options: {disabled: true}
                        },
                        {
                            type: "Control",
                            scope: "#/properties/updatedAt",
                            options: {disabled: true}
                        },
                    ]
                },
            ]
        },
        {
            type: "Group",
            label: "Options",
            elements: [
                {
                    type: "HorizontalLayout",
                    elements: [
                        {
                            type: "Control",
                            scope: "#/properties/organisation",
                            options: {fill: true, disabled: true}
                        },
                        {
                            type: "Control",
                            scope: "#/properties/status",
                            options: {fill: true},
                        },
                        {
                            type: "Control",
                            scope: "#/properties/goLiveDate",
                            options: {fill: true},
                        },
                        {
                            type: "Control",
                            scope: "#/properties/tags",
                            options: {fill: true, renderer: 'tags', availableTags: props.availableTags || []},
                        },
                    ]
                },
                {
                    type: "HorizontalLayout",
                    elements: [
                        {
                            type: "Control",
                            scope: "#/properties/coordinates",
                            options: {fill: true},
                        },
                        {
                            type: "Control",
                            scope: "#/properties/timezone",
                            options: {fill: true},
                        },
                        {
                            type: "Control",
                            scope: "#/properties/currency",
                            options: {fill: true},
                        },
                        {
                            type: "Control",
                            scope: "#/properties/salesDataId",
                            options: {fill: true},
                        },
                    ]
                },
            ]
        },
    ]
})

const RoundedCard = styled(Card)`
    border-radius: 20px;
`;

const OtherConfigSection = styled.div`
    display: flex;
    gap: 1em;
    padding: 12px;
`

const OpeningTimesSection = styled.div`
    flex-shrink: 0;
`

const MapSection = styled.div`
    flex: 1;
    min-width: 0;
    min-height: 300px;
    overflow: hidden;
`

const normaliseOpeningTimes = (openingTimes) => {
    // fill in missing opening times and sort the array
    const filled = openingTimes?.length ?
        openingTimes.sort((a, b) => a.day - b.day)
        : [
            ...Array(7).keys()
        ].reduce((acc, day) => [...acc, {
            day: day,
            startHour: 0,
            startMinute: 0,
            stopHour: 23,
            stopMinute: 59
        }], []);
    // some opening times objects have an ID for some reason...
    return filled.map(ot => {
        const {_id, ...periods} = ot;
        return periods;
    });
};

const OneLoc = ({locid}) => {
    const {setLoading, setNav, setControls} = useContext(DataPageContext);
    const [info, setInfo] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editErrors, setEditErrors] = useState(false);
    const [otErrors, setOtErrors] = useState(false);
    const [options, setOptions] = useState({});
    const savedTagIdsRef = useRef([]);

    const loadTagCatalog = useCallback(async () => {
        const fetched = await entityTags.list({withinOrganisations: info.organisation, targetCollection: 'locations'});
        if (fetched === false) throw new Error('401');
        const sorted = [...fetched].sort((a, b) => a.tagValue.localeCompare(b.tagValue));
        setOptions(prev => ({...prev, availableTags: sorted}));
        return sorted;
    }, [info?.organisation]);

    // editing toggle — update controls and handle save
    useEffect(() => {
        setControls([
            {name: editing ? "Save" : "Edit", onClick: () => setEditing(prev => !prev), icon: editing ? "floppy-disk" : "edit", confirm: editing}
        ]);

        if (!editing && info) {
            if (editErrors || otErrors) {
                errorToast({message: "Some fields are wrong", timeout: 5000});
                setEditing(true);
            } else {
                setLoading({status: true, location: "Saving location..."});

                const applyRefresh = (loc) => {
                    loc.tags = loc.tags || [];
                    loc.openingTimes = normaliseOpeningTimes(loc.openingTimes);
                    savedTagIdsRef.current = loc.tags.map(t => t.id);
                    setInfo(loc);
                };

                (async () => {
                    const selectedIds = info.tags.filter(t => t.id).map(t => t.id);
                    const pending = info.tags.filter(t => !t.id);

                    let createdAny = false;
                    let catalogRefreshed = false;

                    for (const pendingTag of pending) {
                        createdAny = true;
                        try {
                            const created = await entityTags.create({
                                organisation: info.organisation,
                                targetCollection: 'locations',
                                tagType: pendingTag.tagType,
                                tagValue: pendingTag.tagValue,
                            }, {disableErrorToasts: true});
                            if (created === false) throw new Error('401');
                            selectedIds.push(created.id);
                        } catch (err) {
                            if (err.message !== '409') throw err;
                            const catalog = await loadTagCatalog();
                            catalogRefreshed = true;
                            const match = catalog.find(t => t.tagType === pendingTag.tagType && t.tagValue === pendingTag.tagValue);
                            if (!match) throw err;
                            selectedIds.push(match.id);
                        }
                    }

                    const toRemove = savedTagIdsRef.current.filter(id => !selectedIds.includes(id));
                    const toAdd = selectedIds.filter(id => !savedTagIdsRef.current.includes(id));

                    for (const tagId of toRemove) {
                        try {
                            const result = await locations.unassignTag(locid, tagId, {disableErrorToasts: true});
                            if (result === false) throw new Error('401');
                        } catch (err) {
                            if (err.message !== '404') throw err;
                        }
                    }

                    for (const tagId of toAdd) {
                        try {
                            const result = await locations.assignTag(locid, tagId, {disableErrorToasts: true});
                            if (result === false) throw new Error('401');
                        } catch (err) {
                            if (err.message === '404') {
                                await loadTagCatalog();
                                throw new Error("unable to add tag to location as the tag does not exist");
                            }
                            if (err.message !== '409') throw err;
                        }
                    }

                    const updated = await locations.update(locid, {
                        name: info.name,
                        status: info.status,
                        coordinates: info.coordinates,
                        timezone: info.timezone,
                        currency: info.currency,
                        openingTimes: info.openingTimes,
                        goLiveDate: info.goLiveDate,
                        salesDataId: info.salesDataId,
                    });
                    if (updated === false) throw new Error('401');

                    const refreshed = await locations.get(locid);
                    if (refreshed === false) throw new Error('401');
                    applyRefresh(refreshed);

                    if (createdAny && !catalogRefreshed) {
                        await loadTagCatalog();
                    }

                    successToast({message: "Updated " + refreshed.name});
                })().catch(async (err) => {
                    console.log(err);
                    errorToast({message: "Failed to save - " + err.message, timeout: 5000});
                    try {
                        const refetched = await locations.get(locid);
                        if (refetched !== false) applyRefresh(refetched);
                    } catch (refetchErr) {
                        console.log(refetchErr);
                    }
                    setEditing(true);
                }).finally(() => {
                    setLoading({status: false, location: info.name + " (" + locid + ")"});
                });
            }
        }
        return () => setControls([]);
    }, [editing]);

    // load location data
    useEffect(() => {
        console.log("Loading locid: " + locid);
        setLoading({status: true, location: "Loading location: " + locid});

        locations.get(locid, {include: "organisationDetails"}).then(async loadedInfo => {
            console.log("Loaded locid: " + locid);

            // load options
            const availableOrganisations = await organisations.list();

            loadedInfo.openingTimes = normaliseOpeningTimes(loadedInfo.openingTimes);
            loadedInfo.tags = loadedInfo.tags || [];
            savedTagIdsRef.current = loadedInfo.tags.map(t => t.id);

            // Check with Google if the timezone is correct
            if (typeof loadedInfo.coordinates !== "undefined" && loadedInfo.timezone !== "undefined") {
                fetch(
                    `https://maps.googleapis.com/maps/api/timezone/json?location=${loadedInfo.coordinates}&timestamp=${Math.round(+new Date() / 1000)}&key=${GOOGLE_TIMEZONE_API_KEY}`
                ).then(res => res.json()).then(
                    data => {
                        if (loadedInfo.timezone !== data.timeZoneId)
                            window.confirm(`Timezone might be incorrect?\nGoogle says it should be ${data.timeZoneId}`);
                    }
                );
            }

            setOptions({
                availableOrganisations: availableOrganisations.sort((a, b) => a.name.localeCompare(b.name))
            });
            setInfo({...loadedInfo, organisation: loadedInfo.organisation?._id});
            setNav({
                left: loadedInfo.organisation ? [{
                    name: loadedInfo.organisation.name,
                    path: "/orgs/" + loadedInfo.organisation._id,
                    icon: "office",
                    subpaths: [
                        {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + loadedInfo.organisation._id},
                        {name: "Devices", icon: "desktop", path: '/devs?org=' + loadedInfo.organisation._id},
                        {name: "Cameras", icon: "camera", path: '/recs?org=' + loadedInfo.organisation._id},
                        {name: "Users", icon: "people", path: '/orgs/' + loadedInfo.organisation._id + '/users'}
                    ]
                }] : [],
                right: [
                    {name: "Devices", path: "/devs?loc=" + locid, icon: "desktop"},
                    {name: "Cameras", path: "/recs?loc=" + locid, icon: "camera"},
                ]
            });
            setLoading({status: false, location: loadedInfo.name + " (" + loadedInfo.id + ")"});
        }).catch(error => {
            setLoading({status: false, location: "Failed to load location..."});
            console.log("Failed to load: " + error.message);
        });
    }, []);

    // load extra resources (tag catalog) when info changes
    useEffect(() => {
        if (!info?.organisation) return;
        const locLabel = info.name + " (" + info.id + ")";
        setLoading({status: false, location: locLabel + " ... loading extra resources"});
        loadTagCatalog().then(() => {
            setLoading({status: false, location: locLabel});
            console.log("Loaded extra resources (tags)");
        }).catch(error => {
            console.log("Failed to load extra resources: " + error.message);
            setLoading({status: false, location: locLabel + " ... failed to load extra resources"});
        });
    }, [info?.organisation]);

    if (!info) return null;

    const coords = info?.coordinates?.split(",").map(c => parseFloat(c)) || [0, 0];
    const locSchema = _LocSchema({...options});

    return (
        <Fragment>
            <MapsAPIProvider
                apiKey={GOOGLE_MAPS_API_KEY}
                onLoad={() => console.log('Maps API has loaded.')}
            >
                <SchemaForm
                    schema={locSchema}
                    uischema={_UILocSchema({...options})}
                    data={info}
                    readonly={!editing}
                    renderers={{tags: TagsRenderer}}
                    onChange={({data, errors}) => {
                        setInfo(data);
                        setEditErrors(!!errors.length);
                    }}
                />
                <RoundedCard>
                    <OtherConfigSection>
                        <OpeningTimesSection>
                            <OpeningTimes
                                values={info.openingTimes}
                                editing={editing}
                                onChange={(data, errors) => {
                                    setInfo(prev => ({...prev, openingTimes: data}));
                                    setOtErrors(errors);
                                }}
                            />
                        </OpeningTimesSection>
                        <MapSection>
                            <Map
                                mapId={locid}
                                defaultZoom={15}
                                defaultCenter={{lat: coords[0], lng: coords[1]}}
                            >
                                <AdvancedMarker position={{lat: coords[0], lng: coords[1]}}>
                                    <Pin background={'rgb(255,0,0)'} glyphColor={'#000'}
                                         borderColor={'#000'}/>
                                </AdvancedMarker>
                            </Map>
                        </MapSection>
                    </OtherConfigSection>
                </RoundedCard>
            </MapsAPIProvider>
        </Fragment>
    );
}

export {OneLoc};